'use client';
import React, { useEffect, useRef, useState } from 'react';

interface TeamMember {
  name: string;
  title: string;
  description: string;
  initials: string;
  color: string;
}

const teamMembers: TeamMember[] = [
  {
    name: 'Marek Kozlowski',
    title: 'President',
    description:
      'Marek leads Grid Global with over 20 years of experience in commercial finance and brokerage. His visionary leadership and deep industry expertise have positioned Grid Global as a premier commercial brokerage firm.',
    initials: 'MK',
    color: 'bg-brand-navy',
  },
  {
    name: 'Saeed Omar',
    title: 'Loan Manager',
    description:
      'Saeed oversees all loan origination and management operations, bringing 15+ years of expertise in commercial lending. He ensures seamless loan processing and maintains strong relationships with our lending partners.',
    initials: 'SO',
    color: 'bg-brand-blue',
  },
  {
    name: 'Mohammad Khalid',
    title: 'Loan Supervisor',
    description:
      'Mohammad supervises the loan processing team, ensuring compliance, accuracy, and timely delivery of all loan transactions. His meticulous attention to detail and regulatory knowledge are invaluable assets.',
    initials: 'MK',
    color: 'bg-brand-green',
  },
  {
    name: 'Antonio Ferrari',
    title: 'Chief Financial Consultant',
    description:
      'Antonio brings international financial expertise to Grid Global, specializing in complex financial structuring and strategic advisory. His background spans investment banking, private equity, and corporate finance.',
    initials: 'AF',
    color: 'bg-brand-darknavy',
  },
  {
    name: 'Ana Claudia',
    title: 'Financial Secretary',
    description:
      'Ana Claudia manages financial documentation, client records, and administrative operations with exceptional precision. Her organizational expertise ensures smooth day-to-day operations across all departments.',
    initials: 'AC',
    color: 'bg-brand-lightblue',
  },
];

export default function Team() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="team" className="w-full bg-gray-50 py-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-brand-navy mb-4">
            Our Leadership Team
          </h2>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-16 bg-brand-green" />
            <div className="w-2 h-2 rounded-full bg-brand-green" />
            <div className="h-px w-16 bg-brand-green" />
          </div>
          <p className="font-sans text-gray-500 text-lg max-w-2xl mx-auto">
            Meet the experienced professionals driving Grid Global&apos;s success
          </p>
        </div>

        {/* Team Grid */}
        <div ref={ref} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {teamMembers.map((member, index) => (
            <div
              key={member.name}
              className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:shadow-brand-navy/10 border border-gray-100 hover:border-brand-blue/20 transition-all duration-300 hover:-translate-y-1 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms`, transitionDuration: '600ms' }}
            >
              {/* Card Top - colored bar */}
              <div className={`h-1.5 w-full ${member.color}`} />

              <div className="p-6">
                {/* Avatar + Name */}
                <div className="flex items-center gap-4 mb-5">
                  <div
                    className={`w-16 h-16 rounded-full ${member.color} flex items-center justify-center text-white font-serif font-bold text-xl flex-shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300`}
                  >
                    {member.initials}
                  </div>
                  <div>
                    <h3 className="font-serif text-lg font-semibold text-brand-navy leading-tight">
                      {member.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-4 h-0.5 bg-brand-green" />
                      <span className="font-sans text-brand-green text-sm font-medium">
                        {member.title}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="font-sans text-gray-500 text-sm leading-relaxed">
                  {member.description}
                </p>

                {/* Social Links */}
                <div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-2">
                  <a
                    href="#"
                    aria-label={`${member.name} LinkedIn`}
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-brand-blue flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                  <a
                    href="#"
                    aria-label={`${member.name} Email`}
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-brand-navy flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
